'use client';

import React, { useMemo } from 'react';
import VariantTabs from './VariantTabs';
import VariantDetails from './VariantDetails';
import VariantKeySpecs from './VariantKeySpecs';
import ExteriorInteriorSection from './ExteriorInteriorSection';
import FeedbackWidget from '../FeedbackWidget/FeedbackWidget';
import ProductScrollMotion from '../ProductScrollMotion';


export default function ProductModelSwitcher({ variants, initialVariantId, activeVariantId, onVariantChange }) {
  // Use either the passed activeVariantId or default to initialVariantId / first variant
  const currentVariantId = activeVariantId || initialVariantId || (variants.length > 0 ? variants[0].id : null);

  const activeCar = useMemo(() => {
    return variants.find(v => v.id === currentVariantId) || variants[0];
  }, [variants, currentVariantId]);

  const images = useMemo(() => {
    return activeCar?.exterior ? [
      activeCar.exterior.image_1,
      activeCar.exterior.image_2,
      activeCar.exterior.image_3,
      activeCar.exterior.image_4,
      activeCar.exterior.image_5,
      activeCar.exterior.image_6,
    ].filter(Boolean) : [];
  }, [activeCar]);

  if (!activeCar) return null;

  // --- BACKGROUND TEXT LOGIC ---
  // 1. Split by space, grab the first 2 words max
  const carNameParts = activeCar.english_name.split(' ').slice(0, 2);

  // 2. Convert to uppercase for the background display (e.g., "COROLLA CROSS" or "RAV4")
  const displayCarName = carNameParts.join(' ').toUpperCase();

  // 3. Check if it actually has 2 parts so we know to apply the smaller font classes
  const isTwoParts = carNameParts.length > 1;

  // Attempt to get the generic model name (e.g., "RAV4" from "RAV4 2.0L")
  const modelNameText = activeCar.car_model?.name || activeCar.english_name.split(' ')[0];

  // Pick the best image for the hero section
  const heroImage = activeCar.main_image || activeCar.slider_image || activeCar.detail_image_1;

  return (
    <div className="w-full min-h-screen bg-white text-gray-900 overflow-hidden">
      <FeedbackWidget />
      {/* Hero Image Section */}
      <div className="relative w-full z-40 flex flex-col items-center">

        {/* Background and Text Wrapper (Cropped to Hero bounds) */}
        <div className="absolute inset-0 overflow-hidden z-0">
          {/* Background - White to Gray gradient wall on top, White floor on bottom (Hard Horizon) */}
          <div className="absolute inset-0 flex flex-col">
            <div className="h-[60%] md:h-[74%] w-full bg-gradient-to-b from-white to-[#e5e5e5]"></div>
            <div className="h-[40%] md:h-[36%] w-full bg-white"></div>
          </div>

          {/* Background Massive Text - pushed a bit higher above the horizon line */}
          <div className="absolute top-[60%] md:top-[75%] left-0 right-0 flex justify-center z-10 pointer-events-none px-2">
            <h1
              className={`absolute bottom-[18vw] md:bottom-[14vw] xl:bottom-[12vw] w-full text-center font-black uppercase text-[#E1E1E1] tracking-[0.1em] select-none whitespace-nowrap transition-all duration-300 ${isTwoParts
                ? 'text-[8vw] sm:text-[9vw] md:text-[11vw] xl:text-[9vw]' // Smaller sizes for 2-part names
                : 'text-[12vw] sm:text-[14vw] md:text-[20vw] xl:text-[15vw]' // Massive sizes for 1-part names
                }`}
              style={{ lineHeight: '0.8' }}
            >
              {displayCarName}
            </h1>
          </div>
        </div>

        {/* Car PNG - In Normal Document Flow to Push Content Down */}
        <div className="relative z-20 pointer-events-none px-4 pt-12 pb-4 md:pt-24 md:pb-8 flex justify-center w-full">
          <img
            src={heroImage}
            alt={activeCar.english_name}
            className="w-[95%] max-w-[900px] md:max-w-[1400px] h-auto object-contain drop-shadow-2xl"
          />
        </div>
      </div>

      {/* Content Section Below Image */}
      <div className="relative z-30 w-full px-4 md:px-8 mt-12">

        {/* Tabs Below Image */}
        {variants.length > 1 && (
          <VariantTabs
            variants={variants}
            activeVariantId={currentVariantId}
            onSelect={onVariantChange}
          />
        )}

        {/* Title Details Below Tabs */}
        <div className="text-center mb-4">
          <h3 className="text-3xl md:text-5xl font-bold mt-2 text-gray-900 latinNumbers">
            {activeCar.english_name}
          </h3>
          <p className="text-gray-500 mt-2 text-lg font-semibold">{activeCar.fuel_type || 'بنزینی'}</p>

          {/* Manual & Catalog Buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 mt-6 w-full px-4">
            {activeCar?.catalog_file && (
              <a
                href={activeCar.catalog_file}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-900 text-white hover:bg-gray-800 w-full sm:w-auto rounded-button-mob md:rounded-button-desk text-sm font-bold shadow-sm transition-all"
              >
                <span>کاتالوگ</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path></svg>
              </a>
            )}
            {activeCar?.warranty_manual && (
              <a
                href={activeCar.warranty_manual}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 w-full sm:w-auto rounded-button-mob md:rounded-button-desk text-sm font-bold shadow-sm transition-all"
              >
                <span>دفترچه گارانتی</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
              </a>
            )}
            {activeCar?.periodic_maintenance_service && (
              <a
                href={activeCar.periodic_maintenance_service}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 w-full sm:w-auto rounded-button-mob md:rounded-button-desk text-sm font-bold shadow-sm transition-all"
              >
                <span>سرویس‌های ادواری</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
              </a>
            )}
          </div>
        </div>

        <div id="feedback-trigger">
          {/* New 80/20 Layout with Image and 3 Key Specs */}
          <VariantKeySpecs car={activeCar} />
        </div>

        {/* ProductScrollMotion Section */}
        {images && images.length >= 3 && (
          <div className="-mx-4 md:-mx-8">
            <ProductScrollMotion images={images.slice(0, 3)} activeCar={activeCar} />
          </div>
        )}

        {/* Exterior and Interior Tabbed Section */}
        <ExteriorInteriorSection car={activeCar} />

      </div>

    </div>
  );
}